
import Vue from 'vue';
import Component from 'vue-class-component';
import { Prop } from 'vue-property-decorator';
import { namespace } from 'vuex-class';
import { BoPanel, IOnOffHandlers } from 'bonomo';
import { IUser, IUserAddress } from '@/model/User/UserInterface';
import { IShortUser } from '@/model/shortInterfaces';
import { IErrors } from '@/model/IErrors';
import { IAuthUserData } from '@/store/modules/authModule';
import UserNav from '@/components/user/widget/UserNav.vue';
import UserSearch from '@/components/user/widget/UserSearch.vue';
import SimpleConfirmationDialog from '@/components/shared/SimpleConfirmationDialog.vue';
import CreateButton from '@/components/shared/CreateButton.vue';

const userModule = namespace('userModule');
const authModule = namespace('authModule');

@Component({
  name: 'UserAddresses',
  components: {
    BoPanel,
    UserNav,
    UserSearch,
    SimpleConfirmationDialog,
    CreateButton,
  },
})
export default class UserAddresses extends Vue {
  @Prop()
  protected userId!: number;

  @userModule.Getter('user')
  protected user!: IUser;

  @userModule.Getter('addresses')
  protected addresses!: IUserAddress[];

  @userModule.Getter('defaultBillingAddressId')
  protected defaultBillingAddressId!: number | null;

  @userModule.Getter('defaultDeliveryAddressId')
  protected defaultDeliveryAddressId!: number | null;

  @userModule.Getter('errors')
  protected errors!: IErrors;

  @userModule.Action('fetchUser')
  private fetchUser!: (userId: number) => Promise<any>;

  @userModule.Action('fetchAddresses')
  private fetchAddresses!: (userId: number) => Promise<any>;

  @userModule.Action('createAddress')
  private createAddress!: (payload: { userId: number; data: any }) => Promise<any>;

  @userModule.Action('updateAddress')
  private updateAddress!: (payload: { userId: number; addressId: number; data: any }) => Promise<any>;

  @userModule.Action('deleteAddress')
  private deleteAddress!: (payload: { userId: number; addressId: number }) => Promise<any>;

  @userModule.Getter('countries')
  protected countries!: any[];

  @userModule.Action('fetchCountries')
  private fetchCountries!: () => Promise<any>;

  @authModule.Getter('userData')
  protected authUser!: IAuthUserData;

  protected loading: boolean = true;
  private saving: boolean = false;
  private dialogVisible: boolean = false;
  private deleteDialogVisible: boolean = false;
  private editingAddress: IUserAddress | null = null;
  private addressToDelete: IUserAddress | null = null;
  private subscribes: IOnOffHandlers = {};

  private form: any = {};

  private emptyForm() {
    return {
      short_name: '',
      line_1: '',
      line_2: '',
      town_city: '',
      postcode: '',
      country_id: 1,
      phone_number: '',
      address_type: 'residential',
      security_access_code: '',
    };
  }

  get isSelf(): boolean {
    return '' + this.$route.params.userId === '' + this.authUser.id;
  }

  get headers() {
    return [
      { text: this.$t('users.addresses.fields.name'), value: 'name', sortable: true },
      { text: this.$t('users.addresses.fields.address'), value: 'address', sortable: false },
      { text: this.$t('users.addresses.fields.city'), value: 'city', sortable: true },
      { text: this.$t('users.addresses.fields.postcode'), value: 'postcode', sortable: false },
      { text: this.$t('users.addresses.fields.phone'), value: 'phone', sortable: false },
      { text: '', value: 'defaults', sortable: false },
      { text: '', value: 'actions', sortable: false, align: 'end' },
    ];
  }

  get addressTypeOptions() {
    return [
      { text: this.$t('users.addresses.types.residential'), value: 'residential' },
      { text: this.$t('users.addresses.types.commercial'), value: 'commercial' },
    ];
  }

  created() {
    this.subscribes = this.$ons({
      'backend.call.OK.addressCreated': this.onAddressSaved,
      'backend.call.OK.addressUpdated': this.onAddressSaved,
      'backend.call.OK.addressDeleted': this.onAddressDeleted,
      'backend.call.KO.addressCreated': () => { this.saving = false; },
      'backend.call.KO.addressUpdated': () => { this.saving = false; },
    });
  }

  beforeDestroy() {
    this.$offs(this.subscribes);
  }

  loadData() {
    this.fetchUser(this.userId);
    this.fetchAddresses(this.userId);
    this.fetchCountries();
  }

  private onGotoUser(user: IShortUser) {
    if (user.id !== this.userId) {
      this.$router.push({ name: 'userAddresses', params: { userId: '' + user.id } });
      this.loadData();
    }
  }

  private onCreateNew() {
    this.editingAddress = null;
    this.form = this.emptyForm();
    this.dialogVisible = true;
  }

  private onEdit(address: IUserAddress) {
    this.editingAddress = address;
    this.form = {
      short_name: address.name || '',
      line_1: address.line_1 || '',
      line_2: address.line_2 || '',
      town_city: address.city || '',
      postcode: address.postcode || '',
      country_id: address.country_id || 1,
      phone_number: address.phone || '',
      address_type: address.address_type || 'residential',
      security_access_code: address.security_access_code || '',
    };
    this.dialogVisible = true;
  }

  private onSaveAddress() {
    this.saving = true;
    if (this.editingAddress) {
      this.updateAddress({
        userId: this.userId,
        addressId: this.editingAddress.id,
        data: this.form,
      });
    } else {
      this.createAddress({
        userId: this.userId,
        data: this.form,
      });
    }
  }

  private onAddressSaved() {
    this.saving = false;
    this.dialogVisible = false;
    this.fetchAddresses(this.userId);
  }

  private onToggleDefaultBilling(address: IUserAddress) {
    this.updateAddress({
      userId: this.userId,
      addressId: address.id,
      data: { form_action: 'set_default_billing' },
    }).then(() => {
      this.fetchAddresses(this.userId);
    });
  }

  private onToggleDefaultDelivery(address: IUserAddress) {
    this.updateAddress({
      userId: this.userId,
      addressId: address.id,
      data: { form_action: 'set_default_delivery' },
    }).then(() => {
      this.fetchAddresses(this.userId);
    });
  }

  private onDelete(address: IUserAddress) {
    this.addressToDelete = address;
    this.deleteDialogVisible = true;
  }

  private onConfirmDelete() {
    if (!this.addressToDelete) return;
    this.deleteAddress({
      userId: this.userId,
      addressId: this.addressToDelete.id,
    });
  }

  private onAddressDeleted() {
    this.deleteDialogVisible = false;
    this.addressToDelete = null;
    this.fetchAddresses(this.userId);
  }
}
